import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  Loader,
  Modal,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { openConfirmModal } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconArrowLeft, IconCheck, IconClock, IconCopy, IconEye, IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  Firestore,
  Timestamp,
  addDoc,
  collection,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { Functions, httpsCallable } from "firebase/functions";
import DataTable, { TableColumn } from "react-data-table-component";
import { History } from "../../components/history";
import { MyUserDataContext } from "../../contexts";
import { useFirestore, useFunctions } from "../../helpers/firebaseContext";
import { toMantineDate } from "../../helpers/mantineDates";
import { BaseResult, CustomClaims, GenerateXmlRequest } from "../../types";
import {
  ExpenseManagerDoc,
  ExpenseManagerRow,
  buildCreateRows,
  buildDeleteRows,
  buildGenerateXmlRequests,
  formatAmount,
  indicesToDelete,
  occurrenceAmounts,
  occurrences,
  paidProgress,
} from "../../helpers/expenseManager";

const derivedStatus = (docData: ExpenseManagerDoc): "pending" | "active" | "ended" | "deleted" => {
  if (docData.deleted) return "deleted";
  if (docData.status === "pending") return "pending";
  const effectiveEnd = docData.effectiveEndDate ?? docData.endDate;
  return effectiveEnd < dayjs().format("YYYY-MM-DD") ? "ended" : "active";
};

const statusBadge = (docData: ExpenseManagerDoc) => {
  if (docData.deleted) return <Badge color="red" variant="light">Deleted</Badge>;
  if (docData.status === "pending") {
    return <Badge color="blue" variant="light" leftSection={<IconClock size={12} />}>Pending Approval</Badge>;
  }
  const effectiveEnd = docData.effectiveEndDate ?? docData.endDate;
  if (effectiveEnd < dayjs().format("YYYY-MM-DD")) {
    return <Badge color="gray" variant="light">Ended</Badge>;
  }
  if (docData.effectiveEndDate && docData.effectiveEndDate !== docData.endDate) {
    return <Badge color="orange" variant="light">Closing {docData.effectiveEndDate}</Badge>;
  }
  return <Badge color="green" variant="light">Active</Badge>;
};

const genevaCounts = (docData: ExpenseManagerDoc) => {
  const responses = docData.geneva_responses || {};
  let ok = 0;
  let failed = 0;
  Object.values(responses).forEach((response) => {
    if (response?.status?.toLowerCase() === "success") ok += 1;
    else failed += 1;
  });
  return { ok, failed };
};

type ApprovingUser = { uid: string; email: string | null; displayName: string | null };

/** Maker-checker approval: send all journal entries to Geneva, then mark the
 *  doc active. On failure the doc stays pending — re-approving resends
 *  everything, which is safe because InsertUpdate overwrites by KeyValue. */
const approveExpenseManager = async (
  db: Firestore,
  functions: Functions,
  user: ApprovingUser | null | undefined,
  id: string,
  docData: ExpenseManagerDoc,
): Promise<boolean> => {
  const generateXml = httpsCallable<GenerateXmlRequest, BaseResult>(functions, "generateGenevaXml");
  const rows = buildCreateRows(docData, id);
  try {
    for (const request of buildGenerateXmlRequests(rows)) {
      await generateXml({ request, orgId: "demo", isApproval: true });
    }
    await updateDoc(doc(db, "expense_managers", id), {
      status: "active",
      "audit.approvedAt": Date.now(),
      "audit.approvedBy": user?.uid || "",
      "audit.approvedByEmail": user?.email || "",
      "audit.approvedByName": user?.displayName || "",
    });
    await addDoc(collection(db, "expense_managers", id, "history"), {
      msg: `Approved — sent ${rows.length} journal entr${rows.length === 1 ? "y" : "ies"} to Geneva`,
      by: user?.email ?? null,
      byID: user?.uid ?? null,
      date: Timestamp.now(),
    });
    notifications.show({
      title: "Expense manager approved",
      message: `${rows.length} journal entries sent to Geneva.`,
      color: "green",
    });
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    await addDoc(collection(db, "expense_managers", id, "history"), {
      msg: "Geneva send failed on approval",
      details: message,
      by: user?.email ?? null,
      byID: user?.uid ?? null,
      date: Timestamp.now(),
    });
    notifications.show({ title: "Geneva send failed", message, color: "red" });
    return false;
  }
};

const confirmApprove = (docData: ExpenseManagerDoc, onConfirm: () => void) => {
  const count = occurrences(docData.startDate, docData.endDate, docData.frequency).length;
  openConfirmModal({
    title: "Approve Expense Manager",
    centered: true,
    children: (
      <Stack gap="sm">
        <Text size="sm">
          This will send <b>{count}</b> journal entr{count === 1 ? "y" : "ies"} to Geneva
          for <b>{docData.portfolio}</b>, {docData.startDate} → {docData.endDate}.
        </Text>
        {docData.startDate < dayjs().format("YYYY-MM-DD") && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            Some entries are dated in the past and may load into closed accounting periods.
          </Alert>
        )}
      </Stack>
    ),
    labels: { confirm: "Approve & Send", cancel: "Cancel" },
    confirmProps: { color: "green" },
    onConfirm,
  });
};

// ---------------------------------------------------------------------------
// List page
// ---------------------------------------------------------------------------

export default function ExpenseManagers() {
  const navigate = useNavigate();
  const db = useFirestore();
  const functions = useFunctions();
  const userContext = useContext(MyUserDataContext);
  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canManage = Boolean(customClaims.geneva_requests_approve);
  const canCreate = Boolean(customClaims.geneva_requests_create || customClaims.geneva_requests_approve);

  const [rows, setRows] = useState<ExpenseManagerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [portfolioFilter, setPortfolioFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "expense_managers"), (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as ExpenseManagerDoc) }));
      items.sort((a, b) => (b.audit?.createdAt || 0) - (a.audit?.createdAt || 0));
      setRows(items);
      setLoading(false);
    });
    return () => unsub();
  }, [db]);

  const portfolios = useMemo(
    () => Array.from(new Set(rows.map((row) => row.portfolio))).sort(),
    [rows],
  );

  const filteredRows = useMemo(
    () => rows
      .filter((row) => !portfolioFilter || row.portfolio === portfolioFilter)
      .filter((row) => !statusFilter || derivedStatus(row) === statusFilter),
    [rows, portfolioFilter, statusFilter],
  );

  const columns: TableColumn<ExpenseManagerRow>[] = [
    { name: "Portfolio", selector: (row) => row.portfolio, sortable: true, wrap: true },
    { name: "Location Account", selector: (row) => row.locationAccount, sortable: true, wrap: true },
    {
      name: "From → To",
      selector: (row) => `${row.fromAccount} → ${row.toAccount}`,
      wrap: true,
      cell: (row) => (
        <Text size="sm">{row.fromAccount} <Text span c="dimmed">→</Text> {row.toAccount}</Text>
      ),
    },
    {
      name: "Period",
      selector: (row) => `${row.startDate} → ${row.effectiveEndDate ?? row.endDate}`,
      wrap: true,
      cell: (row) => (
        <Text size="sm">
          {row.startDate} → {row.effectiveEndDate ?? row.endDate}
          {row.effectiveEndDate && row.effectiveEndDate !== row.endDate && (
            <Text span c="dimmed" size="xs"> (was {row.endDate})</Text>
          )}
        </Text>
      ),
    },
    { name: "Frequency", selector: (row) => row.frequency, sortable: true },
    { name: "Per Period", selector: (row) => row.perPeriodAmount, format: (row) => formatAmount(row.perPeriodAmount), right: true },
    {
      name: "Paid / Total",
      selector: (row) => paidProgress(row).paid,
      minWidth: "180px",
      cell: (row) => {
        const { paid, total } = paidProgress(row);
        return (
          <Box w="100%">
            <Text size="sm">{formatAmount(paid)} / {formatAmount(total)}</Text>
            <Progress size="xs" value={total > 0 ? (paid / total) * 100 : 0} />
          </Box>
        );
      },
    },
    { name: "Status", cell: (row) => statusBadge(row), width: "110px" },
    {
      name: "Geneva",
      cell: (row) => {
        if (row.status === "pending") return <Text size="sm" c="dimmed">—</Text>;
        const { ok, failed } = genevaCounts(row);
        return (
          <Group gap={4}>
            <Badge color="green" variant="light" size="sm">{ok}✓</Badge>
            {failed > 0 && <Badge color="red" variant="light" size="sm">{failed}✗</Badge>}
          </Group>
        );
      },
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Group gap={4} wrap="nowrap">
          <Tooltip label="View Details">
            <ActionIcon
              variant="subtle"
              onClick={() => navigate(`/geneva_requests/expense_managers/${row.id}`)}
            >
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
          {canManage && derivedStatus(row) === "pending" && (
            <Tooltip label="Approve & send to Geneva">
              <ActionIcon
                variant="subtle"
                color="green"
                loading={approvingId === row.id}
                onClick={(event) => {
                  event.stopPropagation();
                  confirmApprove(row, async () => {
                    setApprovingId(row.id);
                    await approveExpenseManager(db, functions, userContext.user, row.id, row);
                    setApprovingId(null);
                  });
                }}
              >
                <IconCheck size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {canCreate && (
            <Tooltip label="Copy to a new expense manager">
              <ActionIcon
                variant="subtle"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate("/geneva_requests/expense_managers/create", { state: { copyFrom: row } });
                }}
              >
                <IconCopy size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ),
      width: "130px",
      center: true,
    },
  ];

  return (
    <Container fluid px="md" py="md">
      <Group justify="space-between" mb="xl">
        <Box>
          <Button
            variant="subtle"
            size="compact-md"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/geneva_requests")}
          >
            Back to Geneva Requests
          </Button>
          <Title order={2} mt="sm">Expense Managers</Title>
          <Text c="dimmed" size="sm">Recurring PrePaid journal entries in Geneva</Text>
        </Box>
        {canCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate("/geneva_requests/expense_managers/create")}>
            Create Expense Manager
          </Button>
        )}
      </Group>

      <Group mb="md">
        <Select
          placeholder="Filter by portfolio"
          data={portfolios}
          value={portfolioFilter}
          onChange={setPortfolioFilter}
          clearable
          searchable
          w={280}
        />
        <Select
          placeholder="All statuses"
          data={[
            { value: "pending", label: "Pending Approval" },
            { value: "active", label: "Active" },
            { value: "ended", label: "Ended" },
            { value: "deleted", label: "Deleted" },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          w={180}
        />
      </Group>

      <Card shadow="sm" withBorder p={0}>
        <DataTable
          columns={columns}
          data={filteredRows}
          progressPending={loading}
          progressComponent={<Loader />}
          pagination
          highlightOnHover
          pointerOnHover
          onRowClicked={(row) => navigate(`/geneva_requests/expense_managers/${row.id}`)}
          noDataComponent={<Text c="dimmed" p="lg">No expense managers yet.</Text>}
        />
      </Card>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------

export function ExpenseManagerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const db = useFirestore();
  const functions = useFunctions();
  const userContext = useContext(MyUserDataContext);
  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canManage = Boolean(customClaims.geneva_requests_approve);
  const canCreate = Boolean(customClaims.geneva_requests_create || customClaims.geneva_requests_approve);

  const [docData, setDocData] = useState<ExpenseManagerDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [deleteAll, setDeleteAll] = useState(false);
  const [closeDate, setCloseDate] = useState<string | null>(null);
  const [pastConfirmed, setPastConfirmed] = useState(false);
  const [working, setWorking] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(doc(db, "expense_managers", id), (snapshot) => {
      setDocData(snapshot.exists() ? (snapshot.data() as ExpenseManagerDoc) : null);
      setLoading(false);
    });
    return () => unsub();
  }, [db, id]);

  const dates = useMemo(
    () => (docData ? occurrences(docData.startDate, docData.endDate, docData.frequency) : []),
    [docData],
  );
  const amounts = useMemo(() => (docData ? occurrenceAmounts(docData) : []), [docData]);

  if (!id) {
    navigate("/geneva_requests/expense_managers");
    return null;
  }

  if (loading) {
    return <Container size="xl" py="md"><Loader /></Container>;
  }

  if (!docData) {
    return (
      <Container size="xl" py="md">
        <Alert color="red" title="Not found">Expense manager {id} does not exist.</Alert>
      </Container>
    );
  }

  const today = dayjs().format("YYYY-MM-DD");
  const effectiveEnd = docData.effectiveEndDate ?? docData.endDate;
  const { paid, total } = paidProgress(docData);
  const canAct = canManage && !docData.deleted;
  const isPending = docData.status === "pending" && !docData.deleted;

  // Only target entries still live in Geneva — a previous close-early already
  // deleted everything after the current effectiveEnd.
  const pendingIndices = (deleteAll ? indicesToDelete(docData) : (closeDate ? indicesToDelete(docData, closeDate) : []))
    .filter((n) => dates[n] <= effectiveEnd);
  const deletesPastEntries = pendingIndices.some((n) => dates[n] <= today);

  const occurrenceStatus = (n: number) => {
    const response = docData.geneva_responses?.[String(n)];
    if (response) {
      const success = response.status?.toLowerCase() === "success";
      const badge = (
        <Badge
          color={success ? "green" : "red"}
          variant="light"
          style={response.status_message ? { cursor: "help" } : undefined}
        >
          {success ? "Loaded" : "Error"}
        </Badge>
      );
      return response.status_message ? (
        <Tooltip label={response.status_message} multiline maw={420} withArrow>
          {badge}
        </Tooltip>
      ) : badge;
    }
    if (docData.deleted || dates[n] > effectiveEnd) {
      return <Badge color="gray" variant="light">Deleted</Badge>;
    }
    if (isPending) {
      return <Badge color="gray" variant="light">Not Sent</Badge>;
    }
    return <Badge color="yellow" variant="light">Pending</Badge>;
  };

  // Entries that never loaded or errored (and were not deleted by a close):
  // safe to resend since InsertUpdate with the same KeyValue overwrites.
  // Pending managers have sent nothing — approval is their (re)send path.
  const retryIndices = docData.deleted || isPending ? [] : dates
    .map((date, n) => ({ date, n }))
    .filter(({ date }) => date <= effectiveEnd)
    .filter(({ n }) => {
      const response = docData.geneva_responses?.[String(n)];
      return !response || response.status?.toLowerCase() !== "success";
    })
    .map(({ n }) => n);

  const handleRetry = async () => {
    if (!retryIndices.length) return;
    setRetrying(true);
    const user = userContext.user;
    try {
      const generateXml = httpsCallable<GenerateXmlRequest, BaseResult>(functions, "generateGenevaXml");
      const retrySet = new Set(retryIndices);
      const rows = buildCreateRows(docData, id).filter((_, n) => retrySet.has(n));
      for (const request of buildGenerateXmlRequests(rows)) {
        await generateXml({ request, orgId: "demo", isApproval: true });
      }
      await addDoc(collection(db, "expense_managers", id, "history"), {
        msg: `Resent ${rows.length} failed/pending journal entr${rows.length === 1 ? "y" : "ies"} to Geneva`,
        details: `Entries: ${retryIndices.map((n) => dates[n]).join(", ")}`,
        by: user?.email ?? null,
        byID: user?.uid ?? null,
        date: Timestamp.now(),
      });
      notifications.show({
        title: "Resent to Geneva",
        message: `${rows.length} journal entries resent.`,
        color: "green",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await addDoc(collection(db, "expense_managers", id, "history"), {
        msg: "Geneva resend failed",
        details: message,
        by: user?.email ?? null,
        byID: user?.uid ?? null,
        date: Timestamp.now(),
      });
      notifications.show({ title: "Geneva resend failed", message, color: "red" });
    } finally {
      setRetrying(false);
    }
  };

  const handleConfirm = async () => {
    // Pending managers sent nothing to Geneva — deleting is just a doc update.
    if (isPending && deleteAll) {
      setWorking(true);
      const user = userContext.user;
      try {
        await updateDoc(doc(db, "expense_managers", id), {
          deleted: true,
          status: "closed",
          closedAt: Date.now(),
          closedBy: user?.email || "",
        });
        await addDoc(collection(db, "expense_managers", id, "history"), {
          msg: "Deleted (was pending — nothing sent to Geneva)",
          by: user?.email ?? null,
          byID: user?.uid ?? null,
          date: Timestamp.now(),
        });
        notifications.show({
          title: "Expense manager deleted",
          message: "It was still pending approval, so nothing needed to be removed from Geneva.",
          color: "green",
        });
        setCloseModalOpen(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        notifications.show({ title: "Delete failed", message, color: "red" });
      } finally {
        setWorking(false);
      }
      return;
    }

    if (!deleteAll && !closeDate) return;
    if (!pendingIndices.length) {
      notifications.show({
        title: "Nothing to delete",
        message: "No journal entries fall after the selected close date.",
        color: "yellow",
      });
      return;
    }
    setWorking(true);
    const user = userContext.user;
    try {
      const generateXml = httpsCallable<GenerateXmlRequest, BaseResult>(functions, "generateGenevaXml");
      const rows = buildDeleteRows(id, pendingIndices);
      for (const request of buildGenerateXmlRequests(rows)) {
        await generateXml({ request, orgId: "demo", isApproval: true, isUndo: true });
      }

      const update = deleteAll
        ? { deleted: true, status: "closed", closedAt: Date.now(), closedBy: user?.email || "" }
        : { effectiveEndDate: closeDate, closedAt: Date.now(), closedBy: user?.email || "" };
      await updateDoc(doc(db, "expense_managers", id), update);
      await addDoc(collection(db, "expense_managers", id, "history"), {
        msg: deleteAll ? "Deleted all journal entries" : `Closed early at ${closeDate}`,
        details: `Deleted ${pendingIndices.length} journal entr${pendingIndices.length === 1 ? "y" : "ies"} from Geneva${deleteAll ? "" : ` dated after ${closeDate}`}`,
        by: user?.email ?? null,
        byID: user?.uid ?? null,
        date: Timestamp.now(),
      });
      notifications.show({
        title: deleteAll ? "Expense manager deleted" : "Expense manager closed",
        message: `${pendingIndices.length} journal entries removed from Geneva.`,
        color: "green",
      });
      setCloseModalOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await addDoc(collection(db, "expense_managers", id, "history"), {
        msg: "Geneva delete failed",
        details: message,
        by: user?.email ?? null,
        byID: user?.uid ?? null,
        date: Timestamp.now(),
      });
      notifications.show({ title: "Geneva delete failed", message, color: "red" });
    } finally {
      setWorking(false);
    }
  };

  const openModal = (all: boolean) => {
    setDeleteAll(all);
    setCloseDate(null);
    setPastConfirmed(false);
    setCloseModalOpen(true);
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="md">
        <Box>
          <Button
            variant="subtle"
            size="compact-md"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/geneva_requests/expense_managers")}
          >
            Back to Expense Managers
          </Button>
          <Group gap="sm" mt="sm">
            <Title order={2}>Expense Manager</Title>
            {statusBadge(docData)}
          </Group>
        </Box>
        <Group>
          {canCreate && (
            <Button
              variant="outline"
              leftSection={<IconCopy size={16} />}
              onClick={() => navigate("/geneva_requests/expense_managers/create", { state: { copyFrom: { id, ...docData } } })}
            >
              Copy
            </Button>
          )}
          {canAct && (
            <>
              {retryIndices.length > 0 && (
                <Tooltip label="Resends the failed/pending entries; already-loaded entries are untouched (InsertUpdate overwrites by key)." multiline maw={320} withArrow>
                  <Button
                    variant="outline"
                    color="yellow"
                    leftSection={<IconRefresh size={16} />}
                    loading={retrying}
                    onClick={handleRetry}
                  >
                    Retry {retryIndices.length} Failed
                  </Button>
                </Tooltip>
              )}
              {!isPending && (
                <Button variant="outline" color="orange" onClick={() => openModal(false)}>
                  Close Early
                </Button>
              )}
              <Button variant="outline" color="red" leftSection={<IconTrash size={16} />} onClick={() => openModal(true)}>
                Delete All
              </Button>
            </>
          )}
        </Group>
      </Group>

      {isPending && (
        <Alert color="blue" icon={<IconClock size={16} />} title="Waiting for approval" mb="md">
          <Group justify="space-between" align="center">
            <Text size="sm">
              Created by {docData.audit?.createdByEmail}. Nothing has been sent to Geneva yet.
            </Text>
            {canManage && (
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                loading={approving}
                onClick={() => confirmApprove(docData, async () => {
                  setApproving(true);
                  await approveExpenseManager(db, functions, userContext.user, id, docData);
                  setApproving(false);
                })}
              >
                Approve &amp; Send to Geneva
              </Button>
            )}
          </Group>
        </Alert>
      )}

      <Card shadow="sm" withBorder mb="md" p="md">
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
          <Box><Text size="xs" c="dimmed">Portfolio</Text><Text fw={500}>{docData.portfolio}</Text></Box>
          <Box><Text size="xs" c="dimmed">Location Account</Text><Text fw={500}>{docData.locationAccount}</Text></Box>
          <Box><Text size="xs" c="dimmed">From (Debit)</Text><Text fw={500}>{docData.fromAccount}</Text></Box>
          <Box><Text size="xs" c="dimmed">To (Credit)</Text><Text fw={500}>{docData.toAccount}</Text></Box>
          <Box><Text size="xs" c="dimmed">Frequency</Text><Text fw={500}>{docData.frequency}</Text></Box>
          <Box>
            <Text size="xs" c="dimmed">Period</Text>
            <Text fw={500}>
              {docData.startDate} → {effectiveEnd}
              {docData.effectiveEndDate && docData.effectiveEndDate !== docData.endDate && (
                <Text span c="dimmed" size="xs"> (was {docData.endDate})</Text>
              )}
            </Text>
          </Box>
          <Box><Text size="xs" c="dimmed">Per Period</Text><Text fw={500}>{formatAmount(docData.perPeriodAmount)}</Text></Box>
          <Box>
            <Text size="xs" c="dimmed">Paid / Total</Text>
            <Text fw={500}>{formatAmount(paid)} / {formatAmount(total)}</Text>
            <Progress size="xs" mt={4} value={total > 0 ? (paid / total) * 100 : 0} />
          </Box>
          {docData.eventDesc && (
            <Box><Text size="xs" c="dimmed">Event Desc</Text><Text fw={500}>{docData.eventDesc}</Text></Box>
          )}
          <Box><Text size="xs" c="dimmed">Created By</Text><Text fw={500}>{docData.audit?.createdByEmail}</Text></Box>
          {docData.audit?.approvedByEmail && (
            <Box><Text size="xs" c="dimmed">Approved By</Text><Text fw={500}>{docData.audit.approvedByEmail}</Text></Box>
          )}
          {docData.closedBy && (
            <Box><Text size="xs" c="dimmed">Closed By</Text><Text fw={500}>{docData.closedBy}</Text></Box>
          )}
          <Box><Text size="xs" c="dimmed">Comments</Text><Text fw={500}>{docData.comments}</Text></Box>
        </SimpleGrid>
      </Card>

      <Tabs defaultValue="entries">
        <Tabs.List>
          <Tabs.Tab value="entries">Journal Entries</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="entries" pt="md">
          <Card shadow="sm" withBorder p={0}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Key</Table.Th>
                  <Table.Th>Geneva Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dates.map((date, n) => (
                  <Table.Tr key={date} opacity={docData.deleted || date > effectiveEnd ? 0.5 : 1}>
                    <Table.Td>{n + 1}</Table.Td>
                    <Table.Td>{date}</Table.Td>
                    <Table.Td>{formatAmount(amounts[n])}</Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{id}:{n}</Text></Table.Td>
                    <Table.Td>{occurrenceStatus(n)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="history" pt="md">
          <History historyCollection={collection(db, "expense_managers", id, "history")} />
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        title={deleteAll ? "Delete All Journal Entries" : "Close Expense Manager Early"}
        centered
      >
        <Stack gap="md">
          {!deleteAll && (
            <DatePickerInput
              label="New end date"
              description="All journal entries after this date will be deleted from Geneva. You can close again at an even earlier date later."
              required
              minDate={docData.startDate}
              maxDate={effectiveEnd}
              value={closeDate}
              onChange={(value) => { setCloseDate(toMantineDate(value)); setPastConfirmed(false); }}
            />
          )}
          {isPending && deleteAll ? (
            <Text size="sm">
              This expense manager was never sent to Geneva; it will just be marked deleted.
            </Text>
          ) : (deleteAll || closeDate) && (
            pendingIndices.length > 0 ? (
              <Text size="sm">
                This will delete <b>{pendingIndices.length}</b> journal entr{pendingIndices.length === 1 ? "y" : "ies"} from Geneva.
              </Text>
            ) : (
              <Text size="sm" c="dimmed">No journal entries fall after this date — pick an earlier one.</Text>
            )
          )}
          {!isPending && deletesPastEntries && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Deleting past entries">
              You are deleting journal entries dated in the past. The accounting period may already be
              closed, and if this portfolio is a fund this may affect previously published NAVs.
            </Alert>
          )}
          {!isPending && deletesPastEntries && (
            <Checkbox
              label="I understand this deletes past entries and may affect closed periods / older NAVs."
              checked={pastConfirmed}
              onChange={(event) => setPastConfirmed(event.currentTarget.checked)}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCloseModalOpen(false)}>Cancel</Button>
            <Button
              color="red"
              loading={working}
              disabled={isPending && deleteAll ? false : (pendingIndices.length === 0 || (deletesPastEntries && !pastConfirmed))}
              onClick={handleConfirm}
            >
              {deleteAll ? "Delete All" : "Close Early"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
